export interface SSEEventMessage {
  event?: string
  data: string
}

export interface ReadSSEMessagesOptions {
  inactivityTimeoutMs?: number
}

export class SSEInactivityTimeoutError extends Error {
  timeoutMs: number

  constructor(timeoutMs: number) {
    super(`SSE stream was inactive for ${timeoutMs}ms`)
    this.name = 'SSEInactivityTimeoutError'
    this.timeoutMs = timeoutMs
  }
}

function splitCompleteLines(buffer: string): {
  lines: string[]
  remaining: string
} {
  const lines: string[] = []
  let lineStart = 0

  for (let i = 0; i < buffer.length; i += 1) {
    if (buffer[i] !== '\n') continue

    let line = buffer.slice(lineStart, i)
    if (line.endsWith('\r')) {
      line = line.slice(0, -1)
    }

    lines.push(line)
    lineStart = i + 1
  }

  return {
    lines,
    remaining: buffer.slice(lineStart),
  }
}

export async function* readSSEMessages(
  response: Response,
  options: ReadSSEMessagesOptions = {}
): AsyncGenerator<SSEEventMessage> {
  if (!response.body) {
    throw new Error('No response body')
  }
  const reader = response.body.getReader()

  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent: string | undefined
  let dataLines: string[] = []

  const flushEvent = (): SSEEventMessage | null => {
    if (!currentEvent && dataLines.length === 0) {
      return null
    }

    const event: SSEEventMessage = {
      data: dataLines.join('\n'),
    }

    if (currentEvent) {
      event.event = currentEvent
    }

    currentEvent = undefined
    dataLines = []

    return event
  }

  const handleLine = (line: string): SSEEventMessage | null => {
    if (line === '') {
      return flushEvent()
    }

    if (line.startsWith(':')) {
      return null
    }

    const separatorIndex = line.indexOf(':')
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex)
    let value = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1)

    if (value.startsWith(' ')) {
      value = value.slice(1)
    }

    switch (field) {
      case 'event':
        currentEvent = value
        break
      case 'data':
        dataLines.push(value)
        break
      default:
        break
    }

    return null
  }

  async function readNextChunk(): Promise<ReadableStreamReadResult<Uint8Array>> {
    if (!options.inactivityTimeoutMs || options.inactivityTimeoutMs <= 0) {
      return reader.read()
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let timedOut = false

    try {
      const result = await Promise.race([
        reader.read(),
        new Promise<ReadableStreamReadResult<Uint8Array>>((resolve) => {
          timeoutId = setTimeout(() => {
            timedOut = true
            void reader.cancel('SSE inactivity timeout')
            resolve({ done: true, value: undefined })
          }, options.inactivityTimeoutMs)
        }),
      ])

      if (timedOut) {
        throw new SSEInactivityTimeoutError(options.inactivityTimeoutMs)
      }

      return result
    } finally {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }

  try {
    while (true) {
      const { done, value } = await readNextChunk()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      const { lines, remaining } = splitCompleteLines(buffer)
      buffer = remaining

      for (const line of lines) {
        const event = handleLine(line)
        if (event) {
          yield event
        }
      }
    }

    buffer += decoder.decode()

    if (buffer.length > 0) {
      const trailingLine = buffer.endsWith('\r') ? buffer.slice(0, -1) : buffer
      const event = handleLine(trailingLine)
      if (event) {
        yield event
      }
    }

    const finalEvent = flushEvent()
    if (finalEvent) {
      yield finalEvent
    }
  } finally {
    reader.releaseLock()
  }
}
